import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, IconButton } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import DetailDialog from '../components/DetailDialog';
import { getEntityDraftFields } from '../lib/entityForms';

// Full-page add/edit form for entities with too many fields for the shared
// popup dialog to work well (tenders, trainings, news, ...). Reuses the exact
// same DetailDialog logic (validation, uploads, foreign keys, date
// normalization, audit-logged save) via variant="page" — just rendered as
// normal page content instead of a modal.
interface EditConfig {
  table: string;
  listPath: string;
  detailPath?: (id: string) => string;
}

const EDIT_CONFIG: Record<string, EditConfig> = {
  tenders: { table: 'tenders', listPath: '/tenders', detailPath: (id) => `/tenders/${id}` },
  partners: { table: 'partners', listPath: '/partners', detailPath: (id) => `/partners/${id}` },
  contracts: { table: 'contracts', listPath: '/contracts', detailPath: (id) => `/contracts/${id}` },
  fund_system: { table: 'fund_system', listPath: '/fund-system', detailPath: (id) => `/fund-system/${id}` },
  news: { table: 'news', listPath: '/news', detailPath: (id) => `/news/${id}` },
  trainings: { table: 'trainings', listPath: '/trainings', detailPath: (id) => `/trainings/${id}` },
};

const EntityEditPage: React.FC<{ entity: keyof typeof EDIT_CONFIG }> = ({ entity }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const config = EDIT_CONFIG[entity];
  const isEdit = !!id;
  const BackIcon = isRTL ? ArrowForward : ArrowBack;

  const goBack = () => {
    navigate(isEdit && config.detailPath ? config.detailPath(id!) : config.listPath);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={goBack} size="small"><BackIcon /></IconButton>
      </Box>
      <DetailDialog
        open
        variant="page"
        onClose={goBack}
        tableName={config.table}
        recordId={id}
        fields={(getEntityDraftFields(entity, isRTL) || []) as any}
        onSave={goBack}
      />
    </Box>
  );
};

export default EntityEditPage;
